/**
 * Case file with section-based review (doc 11 §3): every section gets the
 * Agree / Correct / Return verbs; the stage ends with a recommendation that
 * lands in the maker-checker approvals inbox.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Localized } from '@servicesai/api-client';
import { GlassButton, GlassCard, GlassInput, GlassSelect, StatusPill, formatDate, useI18n } from '@servicesai/ui';
import { admin } from '../lib/api.js';
import { formatFieldValue } from '../lib/fieldValue.js';
import { useRecordRecentCase } from '../lib/recentCases.js';
import { S } from '../strings.js';
import { HeroBand, SectionCard, StatusDot } from '../components/elevated/kit.js';
import {
  HousingCasePipelinePanel,
  type HousingCasePipelineView,
} from './housing/HousingCasePipelinePanel.js';

interface SectionState {
  status: 'agreed' | 'corrected' | 'returned';
  comment: string | null;
  at: string;
  by: string;
}

interface Assignment {
  id: string;
  stage: string;
  status: string;
  due_at: string | null;
  section_states: Record<string, SectionState>;
}

interface CaseSection {
  id: string;
  label: Localized;
  kind: 'fields' | 'documents' | 'eligibility';
  fields?: { id: string; label: Localized; value: unknown; provenance: Record<string, unknown> | null }[];
  documents?: {
    requirementId: string;
    name: Localized;
    attached: boolean;
    satisfiedVia?: string;
    document?: { filename: string | null; status: string; extracted: Record<string, string>; confidence: number | null };
    documents?: {
      filename: string | null;
      status: string;
      extracted: Record<string, string>;
      confidence: number | null;
    }[];
  }[];
  eligibility?: { verdict: string; reasons?: Localized[]; evaluatedAt?: string } | null;
}

interface CaseFile {
  application: {
    id: string;
    reference: string;
    state: string;
    lang: string;
    channel: string;
    submitted_at: string | null;
    sla_due_at: string | null;
    decision: { outcome?: string } | null;
  };
  spec: { name: Localized; fields: { id: string; label: Localized; type: string }[] };
  serviceSlug: string;
  assignments: Assignment[];
  sections: CaseSection[];
  events: { seq: number; event: string; payload: Record<string, unknown>; created_at: string }[];
  transcript: {
    role: string;
    direction: string;
    content: string | null;
    media?: { filename: string; mime: string; url: string | null; documentId: string | null }[];
    created_at: string;
  }[];
  housingCase?: HousingCasePipelineView | null;
}

const STATE_LABEL: Record<string, Localized> = {
  submitted: { en: 'Submitted', ar: 'تم التقديم' },
  in_review: { en: 'In review', ar: 'قيد المراجعة' },
  needs_citizen_input: { en: 'Waiting on citizen', ar: 'بانتظار المتعامل' },
  approved: { en: 'Approved', ar: 'تمت الموافقة' },
  rejected: { en: 'Rejected', ar: 'مرفوض' },
};

export function CasePage() {
  const { reference = '' } = useParams();
  const { l, lang } = useI18n();
  const [file, setFile] = useState<CaseFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Sticky navigator (point 27): the section currently scrolled into view, and
  // the decision bar's pending correct/return request for that section.
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [verbRequest, setVerbRequest] = useState<{ sectionId: string; mode: 'correct' | 'return'; nonce: number } | null>(null);
  const decisionRef = useRef<{ agree: () => void; correct: () => void; ret: () => void }>({
    agree: () => undefined,
    correct: () => undefined,
    ret: () => undefined,
  });
  useRecordRecentCase(reference);

  const load = useCallback(() => {
    admin()
      .caseFile(reference)
      .then((data) => setFile(data as unknown as CaseFile))
      .catch((err: Error) => setError(err.message));
  }, [reference]);

  useEffect(load, [load]);

  // The open assignment drives the verbs; historical ones stay read-only.
  const assignment = useMemo(() => {
    const open = (file?.assignments ?? []).filter((a) => a.status === 'queued' || a.status === 'in_progress');
    return open.length > 0 ? open[open.length - 1]! : null;
  }, [file]);

  const act = useCallback(
    async (fn: () => Promise<unknown>, okMessage?: string) => {
      try {
        await fn();
        if (okMessage) setToast(okMessage);
        load();
      } catch (err) {
        setToast(`${l(S.actionFailed)}: ${(err as Error).message}`);
      }
    },
    [load, l],
  );

  // Highlight the navigator entry for the section currently in view.
  useEffect(() => {
    if (!file) return;
    const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-case-section]'));
    if (nodes.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection((entry.target as HTMLElement).dataset.caseSection ?? null);
          }
        }
      },
      { rootMargin: '-25% 0px -65% 0px' },
    );
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [file]);

  // Decision shortcuts (point 29): A agree · C correct · R return, ignored
  // while typing in any field or when modifier keys are held.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Never steal keys while an overlay (palette, cheat-sheet, drawer) is open.
      if (document.querySelector('[role="dialog"]')) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable)
      ) {
        return;
      }
      const key = event.key.toLocaleLowerCase();
      if (key === 'a') decisionRef.current.agree();
      else if (key === 'c') decisionRef.current.correct();
      else if (key === 'r') decisionRef.current.ret();
      else return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (error) {
    return <GlassCard className="p-6 text-sm text-reject">{error}</GlassCard>;
  }
  if (!file) return <div className="h-96 animate-pulse rounded-xl border border-aeblack-100 bg-white" aria-busy="true" />;

  const { application } = file;
  const allReviewed =
    assignment !== null &&
    file.sections.every((section) => {
      const state = assignment.section_states[section.id];
      return state && (state.status === 'agreed' || state.status === 'corrected');
    });
  const slaMs = application.sla_due_at && !application.decision ? new Date(application.sla_due_at).getTime() - Date.now() : null;
  const slaOverdue = slaMs !== null && slaMs < 0;
  const slaHours = slaMs === null ? 0 : Math.max(0, Math.round(Math.abs(slaMs) / 3_600_000));

  // Review progress + the highlighted section drive the navigator, the ring
  // and the sticky decision bar (points 27–29).
  const reviewedCount = file.sections.filter((section) => {
    const state = assignment?.section_states[section.id];
    return state !== undefined && (state.status === 'agreed' || state.status === 'corrected');
  }).length;
  const currentSectionId =
    activeSection && file.sections.some((section) => section.id === activeSection)
      ? activeSection
      : (file.sections[0]?.id ?? null);
  const currentSection = file.sections.find((section) => section.id === currentSectionId) ?? null;

  const scrollToSection = (sectionId: string) => {
    document.getElementById(`case-section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const agreeCurrent = () => {
    if (!assignment || !currentSection) return;
    void act(() => admin().sectionState(assignment.id, currentSection.id, 'agreed'));
  };
  const requestVerbMode = (mode: 'correct' | 'return') => {
    if (!assignment || !currentSection) return;
    setVerbRequest({ sectionId: currentSection.id, mode, nonce: Date.now() });
    scrollToSection(currentSection.id);
  };
  decisionRef.current = {
    agree: agreeCurrent,
    correct: () => {
      if (currentSection?.kind === 'fields') requestVerbMode('correct');
    },
    ret: () => requestVerbMode('return'),
  };

  return (
    <div>
      <HeroBand
        accent="#00608d"
        eyebrow={l(S.caseFile)}
        breadcrumbs={
          <>
            <Link to="/queues" className="transition hover:text-white">{l(S.queues)}</Link>
            <span className="mx-1.5" aria-hidden>›</span>
            <span className="font-mono" dir="ltr">{application.reference}</span>
          </>
        }
        title={<span className="font-mono" dir="ltr">{application.reference}</span>}
        subtitle={
          <>
            <span className="font-semibold text-white/85">{l(file.spec.name)}</span>
            {application.submitted_at && (
              <span className="mt-1 block text-white/60">{l(S.submitted)}: {formatDate(application.submitted_at, lang)}</span>
            )}
          </>
        }
        status={
          <>
            <StatusPill
              state={application.state}
              label={l(STATE_LABEL[application.state] ?? { en: application.state, ar: application.state })}
            />
            {application.sla_due_at && !application.decision && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wide text-white/85">
                <StatusDot tone={slaOverdue ? 'down' : 'warn'} />
                {l(S.due)}: {formatDate(application.sla_due_at, lang)}
                {slaOverdue ? ` · ${l(S.overdue)}` : ` · ${slaHours}h`}
              </span>
            )}
            <span className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1.5 font-mono text-[10px] font-extrabold uppercase tracking-wide text-white/75">
              {application.channel} · {application.lang}
            </span>
          </>
        }
        actions={
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex min-h-10 cursor-pointer items-center rounded-xl border border-white/20 bg-white/10 px-4 text-xs font-extrabold text-white transition hover:bg-white/20"
          >
            {l({ en: 'Print case packet', ar: 'طباعة ملف الحالة' })}
          </button>
        }
      />

      {toast && (
        <div
          className="mt-3 cursor-pointer rounded-2xl border border-gold/40 bg-gold/10 px-4 py-2 text-xs font-bold text-gold-deep"
          onClick={() => setToast(null)}
        >
          {toast}
        </div>
      )}

      {file.serviceSlug === 'housing-assistance-request' && (
        <HousingCasePipelinePanel
          reference={reference}
          applicationState={application.state}
          housingCase={file.housingCase ?? null}
          act={act}
        />
      )}

      <div className="mt-4 grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_320px_230px]">
        {/* Sections */}
        <div className="sticky top-24 space-y-4">
          <h2 className="text-lg font-extrabold">{l(S.sections)}</h2>
          {file.sections.map((section) => (
            <div
              key={section.id}
              id={`case-section-${section.id}`}
              data-case-section={section.id}
              className="scroll-mt-28"
            >
              <ReviewSection
                section={section}
                assignment={assignment}
                specFields={file.spec.fields}
                reference={reference}
                act={act}
                requestedMode={verbRequest?.sectionId === section.id ? verbRequest.mode : null}
                requestNonce={verbRequest?.nonce ?? 0}
              />
            </div>
          ))}

          {assignment ? (
            <RecommendCard reference={reference} assignmentId={assignment.id} ready={allReviewed} act={act} />
          ) : (
            !application.decision && (
              <GlassCard className="p-4 text-xs text-ink-faint">{l(S.noAssignment)}</GlassCard>
            )
          )}

          {/* Sticky decision bar (point 29): the three verbs applied to the
              section currently highlighted in the navigator. */}
          {assignment && currentSection && (
            <div className="sticky bottom-4 z-20 rounded-2xl border border-aeblack-200 bg-white/95 p-3 shadow-[0_18px_40px_-20px_rgb(18_42_56/0.45)] backdrop-blur">
              <div className="flex flex-wrap items-center gap-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[11px] font-extrabold">{l(currentSection.label)}</div>
                  <div className="text-[9px] leading-snug text-ink-faint">{l(S.decisionBarHint)}</div>
                </div>
                <GlassButton variant="primary" className="!min-h-11 !px-4 text-sm" onClick={agreeCurrent}>
                  ✓ {l(S.agree)}
                  <kbd className="ms-1.5 rounded border border-white/40 px-1 font-mono text-[9px]">A</kbd>
                </GlassButton>
                <GlassButton
                  className="!min-h-11 !px-4 text-sm"
                  disabled={currentSection.kind !== 'fields'}
                  onClick={() => requestVerbMode('correct')}
                >
                  ✎ {l(S.correct)}
                  <kbd className="ms-1.5 rounded border border-aeblack-200 px-1 font-mono text-[9px]">C</kbd>
                </GlassButton>
                <GlassButton variant="danger" className="!min-h-11 !px-4 text-sm" onClick={() => requestVerbMode('return')}>
                  ↩ {l(S.returnVerb)}
                  <kbd className="ms-1.5 rounded border border-white/40 px-1 font-mono text-[9px]">R</kbd>
                </GlassButton>
              </div>
            </div>
          )}
        </div>

        {/* Side: transcript + timeline */}
        <div className="space-y-4">
          <SectionCard accent="#0173ab" title={l(S.transcript)}>
            <div className="max-h-80 space-y-2 overflow-y-auto pe-1">
              {file.transcript
                .filter((m) => m.content || (m.media && m.media.length > 0))
                .map((m, i) => (
                  <div
                    key={i}
                    className={[
                      'max-w-[90%] rounded-xl px-3 py-2 text-xs leading-relaxed',
                      m.direction === 'inbound' ? 'bubble-citizen ms-auto' : 'bubble-concierge',
                    ].join(' ')}
                  >
                    {m.content}
                    {m.media && m.media.length > 0 ? (
                      <div className="mt-1.5 space-y-1">
                        {m.media.map((item, j) => (
                          <div key={j}>
                            {(item.mime ?? '').startsWith('image/') && item.url ? (
                              <a href={item.url} target="_blank" rel="noreferrer" className="block">
                                <img
                                  src={item.url}
                                  alt={item.filename}
                                  className="max-h-32 max-w-full rounded-md object-contain"
                                />
                              </a>
                            ) : null}
                            <div className="text-[10px] font-semibold text-ink-soft">
                              {l({ en: 'Attachment', ar: 'مرفق' })}: {item.filename}
                              {item.url ? (
                                <>
                                  {' · '}
                                  <a href={item.url} target="_blank" rel="noreferrer" className="text-oasis">
                                    Open
                                  </a>
                                </>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                 ))}
            </div>
          </SectionCard>

          <SectionCard accent="#0173ab" title={l(S.timeline)}>
            <ol className="relative space-y-2.5 border-s-2 border-ink/10 ps-4">
              {file.events.map((event) => (
                <li key={event.seq} className="relative">
                  <span className="absolute -start-[21px] top-1 h-2 w-2 rounded-full bg-oasis" />
                  <div className="text-xs font-semibold">{event.event}</div>
                  <div className="text-[10px] text-ink-faint">{formatDate(event.created_at, lang)}</div>
                </li>
              ))}
            </ol>
          </SectionCard>
        </div>

        {/* Sticky section navigator (points 27–28): per-section review state,
            live progress ring, smooth-scroll jumps. xl+ only. */}
        <aside className="sticky top-24 hidden xl:block">
          <div className="rounded-2xl border border-aeblack-100 bg-white p-3 shadow-[0_2px_12px_-6px_rgb(14_15_18/0.1)]">
            <div className="flex items-center gap-2.5 border-b border-aeblack-100 pb-2.5">
              <ProgressRing reviewed={reviewedCount} total={file.sections.length} label={l(S.reviewProgress)} />
              <div className="min-w-0">
                <div className="text-[11px] font-extrabold">{l(S.sectionNavigator)}</div>
                <div className="font-mono text-[9px] text-ink-faint" dir="ltr">
                  {reviewedCount}/{file.sections.length}
                </div>
              </div>
            </div>
            <nav className="mt-2 space-y-1" aria-label={l(S.sectionNavigator)}>
              {file.sections.map((section) => {
                const state = assignment?.section_states[section.id];
                const status = state?.status ?? 'pending';
                const statusColor =
                  status === 'agreed'
                    ? '#3f8e50'
                    : status === 'corrected'
                      ? '#b68a35'
                      : status === 'returned'
                        ? '#d83731'
                        : '#9ea2a9';
                const statusLabel =
                  status === 'agreed'
                    ? l(S.agreed)
                    : status === 'corrected'
                      ? l(S.corrected)
                      : status === 'returned'
                        ? l(S.returnedState)
                        : l(S.pendingState);
                const current = currentSectionId === section.id;
                return (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => scrollToSection(section.id)}
                    aria-current={current ? 'true' : undefined}
                    className={`flex w-full cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-start transition ${
                      current ? 'bg-aeblack-800 text-white' : 'text-ink-soft hover:bg-aeblack-50 hover:text-ink'
                    }`}
                  >
                    <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: statusColor }} />
                    <span className="min-w-0 flex-1 truncate text-[11px] font-semibold">{l(section.label)}</span>
                    <span className={`shrink-0 text-[9px] font-bold ${current ? 'text-white/70' : 'text-ink-faint'}`}>
                      {statusLabel}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>
      </div>
    </div>
  );
}

/** Mini SVG review-progress ring (point 28): reviewed/total sections with %. */
function ProgressRing({ reviewed, total, label }: { reviewed: number; total: number; label: string }) {
  const pct = total === 0 ? 0 : reviewed / total;
  const radius = 14;
  const circumference = 2 * Math.PI * radius;
  return (
    <span
      className="relative grid h-11 w-11 shrink-0 place-items-center"
      role="img"
      aria-label={`${label}: ${Math.round(pct * 100)}%`}
    >
      <svg width="44" height="44" viewBox="0 0 36 36" aria-hidden>
        <circle cx="18" cy="18" r={radius} fill="none" stroke="#e1e3e5" strokeWidth="3.5" />
        <circle
          cx="18"
          cy="18"
          r={radius}
          fill="none"
          stroke="#3f8e50"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeDasharray={`${pct * circumference} ${circumference}`}
          transform="rotate(-90 18 18)"
          className="transition-all duration-300"
        />
      </svg>
      <span className="absolute font-mono text-[9px] font-extrabold text-ink">{Math.round(pct * 100)}%</span>
    </span>
  );
}

// ---------------------------------------------------------------------------

function ReviewSection({
  section,
  assignment,
  specFields,
  reference,
  act,
  requestedMode,
  requestNonce,
}: {
  section: CaseSection;
  assignment: Assignment | null;
  specFields: { id: string; label: Localized; type: string }[];
  reference: string;
  act: (fn: () => Promise<unknown>, ok?: string) => Promise<void>;
  /** Mode requested from the sticky decision bar (null = no external request). */
  requestedMode: 'correct' | 'return' | null;
  requestNonce: number;
}) {
  const { l, lang } = useI18n();
  const [mode, setMode] = useState<'none' | 'correct' | 'return'>('none');
  const state = assignment?.section_states[section.id];

  useEffect(() => {
    if (requestedMode) setMode(requestedMode);
  }, [requestedMode, requestNonce]);

  const verb = (status: 'agreed' | 'corrected' | 'returned', comment?: string) =>
    act(() => admin().sectionState(assignment!.id, section.id, status, comment));

  return (
    <SectionCard
      accent="#0173ab"
      title={l(section.label)}
      actions={
        state ? (
          <StatusPill
            state={state.status === 'agreed' ? 'approved' : state.status === 'corrected' ? 'needs_citizen_input' : 'returned'}
            label={l(state.status === 'agreed' ? S.agreed : state.status === 'corrected' ? S.corrected : S.returnedState)}
          />
        ) : undefined
      }
    >
      {/* Content by kind */}
      {section.kind === 'fields' && (
        <dl className="mt-3 space-y-2">
          {section.fields?.map((field) => (
            <div key={field.id} className="flex items-baseline justify-between gap-4 border-b border-aeblack-100 pb-1.5">
              <dt className="text-xs text-ink-soft">{l(field.label)}</dt>
              <dd className="whitespace-pre-line text-end text-sm font-semibold">
                {formatFieldValue(field.value)}
                <span className="ms-2 rounded-full bg-ink/6 px-1.5 py-0.5 text-[9px] font-bold text-ink-faint uppercase">
                  {provenanceLabel(field.provenance, lang)}
                </span>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {section.kind === 'documents' && (
        <div className="mt-3 space-y-2">
          {section.documents?.map((doc) => (
            <div key={doc.requirementId} className="rounded-xl bg-aeblack-50 p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-semibold">📄 {l(doc.name)}</div>
                <StatusPill
                  state={doc.attached ? 'approved' : 'pending'}
                  label={l(doc.attached ? S.attachedDoc : S.missingDoc)}
                />
              </div>
              {(doc.documents ??
                (doc.document ? [doc.document] : [])
              ).map((document, index) => (
                <div
                  key={`${document.filename ?? 'document'}-${index}`}
                  className="mt-2 border-t border-aeblack-100 pt-2 text-[11px] text-ink-soft"
                >
                  <span className="me-1 font-bold tabular-nums">
                    {index + 1}.
                  </span>
                  <span className="font-mono" dir="ltr">
                    {document.filename ?? '—'}
                  </span>
                  {document.confidence !== null && (
                    <span className="ms-2">
                      {l(S.confidence)}:{' '}
                      {Math.round((document.confidence ?? 0) * 100)}%
                    </span>
                  )}
                  {Object.keys(document.extracted).length > 0 && (
                    <div className="mt-1">
                      <span className="font-bold">{l(S.extracted)}: </span>
                      {Object.entries(document.extracted)
                        .map(([key, value]) => `${key}=${value}`)
                        .join(' · ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {section.kind === 'eligibility' && (
        <div className="mt-3">
          {section.eligibility ? (
            <>
              <StatusPill
                state={
                  section.eligibility.verdict === 'eligible'
                    ? 'approved'
                    : section.eligibility.verdict === 'ineligible'
                      ? 'rejected'
                      : 'in_review'
                }
                label={section.eligibility.verdict}
              />
              <ul className="mt-2 list-disc ps-5 text-xs text-ink-soft">
                {section.eligibility.reasons?.map((reason, i) => <li key={i}>{l(reason)}</li>)}
              </ul>
            </>
          ) : (
            <div className="text-xs text-ink-faint">—</div>
          )}
        </div>
      )}

      {/* Verbs */}
      {assignment && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-aeblack-100 pt-3">
          <GlassButton variant="primary" className="!px-4 !py-1.5 text-xs" onClick={() => verb('agreed')}>
            ✓ {l(S.agree)}
          </GlassButton>
          {section.kind === 'fields' && (
            <GlassButton
              className="!px-4 !py-1.5 text-xs"
              onClick={() => setMode(mode === 'correct' ? 'none' : 'correct')}
            >
              ✎ {l(S.correct)}
            </GlassButton>
          )}
          <GlassButton
            className="!px-4 !py-1.5 text-xs"
            onClick={() => setMode(mode === 'return' ? 'none' : 'return')}
          >
            ↩ {l(S.returnVerb)}
          </GlassButton>
        </div>
      )}

      {mode === 'correct' && section.kind === 'fields' && (
        <CorrectForm
          fields={specFields.filter((f) => section.fields?.some((sf) => sf.id === f.id))}
          currentValues={Object.fromEntries((section.fields ?? []).map((field) => [field.id, field.value]))}
          onSubmit={async (fieldId, value, reason) => {
            await act(async () => {
              await admin().correct(reference, fieldId, value, reason);
              await admin().sectionState(assignment!.id, section.id, 'corrected', reason);
            });
            setMode('none');
          }}
          onCancel={() => setMode('none')}
        />
      )}

      {mode === 'return' && assignment && (
        <ReturnForm
          onSubmit={async (comment) => {
            await act(async () => {
              await admin().returnToCitizen(reference, assignment.id, comment);
              await admin().sectionState(assignment.id, section.id, 'returned', comment.en);
            });
            setMode('none');
          }}
          onCancel={() => setMode('none')}
        />
      )}
    </SectionCard>
  );
}

function provenanceLabel(provenance: Record<string, unknown> | null, lang: 'en' | 'ar'): string {
  if (!provenance) return lang === 'ar' ? 'غير معروف' : 'unknown';
  if (provenance.source === 'staff_correction') return lang === 'ar' ? 'تصحيح موظف' : 'staff';
  const channel = (provenance.channel as string) ?? '';
  return channel || (lang === 'ar' ? 'المتعامل' : 'citizen');
}

function CorrectForm({
  fields,
  currentValues,
  onSubmit,
  onCancel,
}: {
  fields: { id: string; label: Localized }[];
  currentValues: Record<string, unknown>;
  onSubmit: (fieldId: string, value: string, reason: string) => Promise<void>;
  onCancel: () => void;
}) {
  const { l } = useI18n();
  const [fieldId, setFieldId] = useState(fields[0]?.id ?? '');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const previous = currentValues[fieldId];
  const previousText = formatFieldValue(previous);
  // Before/after inline diff (point 30): only once the editor value diverges.
  const showDiff = value.trim() !== '' && value !== previousText;
  return (
    <div className="mt-3 rounded-2xl bg-aeblack-50 p-4">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="text-xs font-semibold text-ink-soft">
          {l(S.fieldToCorrect)}
          <GlassSelect
            value={fieldId}
            onChange={(e) => setFieldId(e.target.value)}
            className="mt-1 w-full"
          >
            {fields.map((f) => (
              <option key={f.id} value={f.id}>
                {l(f.label)}
              </option>
            ))}
          </GlassSelect>
        </label>
        <label className="text-xs font-semibold text-ink-soft">
          {l(S.newValue)}
          <GlassInput value={value} onChange={(e) => setValue(e.target.value)} className="mt-1" />
        </label>
      </div>
      {showDiff && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]" aria-live="polite">
          <span className="font-semibold text-ink-faint">{l(S.previousValue)}:</span>
          <span className="rounded bg-aered-50 px-1.5 py-0.5 font-semibold text-reject line-through decoration-reject/60">
            {previousText}
          </span>
          <svg
            aria-hidden
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-ink-faint rtl:-scale-x-100"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
          <span className="rounded bg-aegreen-50 px-1.5 py-0.5 font-bold text-approve">{value}</span>
        </div>
      )}
      <label className="mt-2 block text-xs font-semibold text-ink-soft">
        {l(S.reason)}
        <GlassInput value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" />
      </label>
      <div className="mt-3 flex gap-2">
        <GlassButton
          variant="primary"
          className="!px-4 !py-1.5 text-xs"
          disabled={!fieldId || !value || !reason}
          onClick={() => void onSubmit(fieldId, value, reason)}
        >
          {l(S.saveCorrection)}
        </GlassButton>
        <GlassButton variant="ghost" className="!px-4 !py-1.5 text-xs" onClick={onCancel}>
          {l(S.cancel)}
        </GlassButton>
      </div>
    </div>
  );
}

function ReturnForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (comment: { en: string; ar: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const { l } = useI18n();
  const [en, setEn] = useState('');
  const [ar, setAr] = useState('');
  return (
    <div className="mt-3 rounded-2xl bg-aeblack-50 p-4">
      <div className="mb-1 text-xs font-bold">{l(S.returnTitle)}</div>
      <div className="mb-2 text-[11px] text-ink-faint">{l(S.returnHelp)}</div>
      <label className="block text-xs font-semibold text-ink-soft">
        {l(S.commentEn)}
        <GlassInput value={en} onChange={(e) => setEn(e.target.value)} className="mt-1" dir="ltr" />
      </label>
      <label className="mt-2 block text-xs font-semibold text-ink-soft">
        {l(S.commentAr)}
        <GlassInput value={ar} onChange={(e) => setAr(e.target.value)} className="mt-1" dir="rtl" />
      </label>
      <div className="mt-3 flex gap-2">
        <GlassButton
          variant="danger"
          className="!px-4 !py-1.5 text-xs"
          disabled={!en.trim() || !ar.trim()}
          onClick={() => void onSubmit({ en: en.trim(), ar: ar.trim() })}
        >
          {l(S.sendReturn)}
        </GlassButton>
        <GlassButton variant="ghost" className="!px-4 !py-1.5 text-xs" onClick={onCancel}>
          {l(S.cancel)}
        </GlassButton>
      </div>
    </div>
  );
}

function RecommendCard({
  reference,
  assignmentId,
  ready,
  act,
}: {
  reference: string;
  assignmentId: string;
  ready: boolean;
  act: (fn: () => Promise<unknown>, ok?: string) => Promise<void>;
}) {
  const { l } = useI18n();
  const [reasonEn, setReasonEn] = useState('');
  const [reasonAr, setReasonAr] = useState('');

  const send = (outcome: 'approved' | 'rejected') => {
    const reasons =
      reasonEn.trim() && reasonAr.trim() ? [{ en: reasonEn.trim(), ar: reasonAr.trim() }] : [];
    void act(() => admin().recommend(reference, assignmentId, outcome, reasons), l(S.sentToApprover));
  };

  return (
    <SectionCard accent="#cba344" title={l(S.recommend)} hint={l(S.recommendHelp)}>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <label className="text-xs font-semibold text-ink-soft">
          {l(S.reasonEn)}
          <GlassInput value={reasonEn} onChange={(e) => setReasonEn(e.target.value)} className="mt-1" dir="ltr" />
        </label>
        <label className="text-xs font-semibold text-ink-soft">
          {l(S.reasonAr)}
          <GlassInput value={reasonAr} onChange={(e) => setReasonAr(e.target.value)} className="mt-1" dir="rtl" />
        </label>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <GlassButton variant="primary" disabled={!ready} onClick={() => send('approved')}>
          ✓ {l(S.recommendApprove)}
        </GlassButton>
        <GlassButton variant="danger" disabled={!ready || !reasonEn.trim() || !reasonAr.trim()} onClick={() => send('rejected')}>
          ✗ {l(S.recommendReject)}
        </GlassButton>
      </div>
    </SectionCard>
  );
}
